<template>
  <div>
    <div class="image-upload">
      <input ref="imageSelect" type="file" :accept="inputAccept" :name="inputName" @change="readImage" />

      <div v-if="imageSrc" class="image-upload__img">
        <img :src="imageSrc" alt="" />
        <div v-if="loading" class="image-upload__loader">
          <GLoading size="large" />
        </div>
        <div v-else class="action__hover">
          <div class="image-upload__action d-flex">
            <div class="d-flex align-items-center image-upload__action-upload" @click="onClick">
              <i class="icon icon-upload text-light-high image-upload__action-upload-icon mr-1"></i>
              <p
                class="paragraph-12-medium text-light-high image-upload__action-upload-text"
                :class="{ 'button-tooltip': isDisable }">
                Replace Image
                <template v-if="isDisable">
                  <span class="button-tooltip__content"
                    >This feature is locked <br />
                    with your current plan</span
                  >
                </template>
              </p>
            </div>
            <div class="image-upload__action-delete ml-1" @click="onDelete">
              <p class="paragraph-12-medium text-light1">Delete</p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="image-upload__empty">
        <div class="image-upload image-upload__action d-flex align-items-center">
          <div class="image-upload__action-upload" @click="onClick">
            <i class="icon icon-upload text-light-high image-upload__action-upload-icon mr-1"></i>
            <p
              v-if="isDisable"
              class="paragraph-12-medium image-upload__disable button-tooltip image-upload__action-upload-text">
              Upload Image
              <span class="button-tooltip__content"
                >This feature is locked <br />
                with your current plan</span
              >
            </p>
            <p v-else class="paragraph-12-medium">Upload Image</p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="errorMessage.length"
      class="bg-light2 paragraph-14-regular text-warning-dark d-flex align-items-start justify-content-start image-upload__img-error px-1 pt-1">
      <i class="icon icon-warn-triangle"></i>
      <span class="image-upload__img-error-text ml-1">{{ errorMessage }}</span>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import replaceSpecialCharInImageName from '@/app/utils/replaceSpecialCharInImageName';
import api from '@/app/endpoints.js';
import { POST } from '@/app/api/index';
import config from '@/app/editor/config';
import HELPER from '@/app/utils/HELPER';
import replaceImageToQuality from '@/app/utils/replaceImageToQuality';

const DOMAIN_UPLOAD = config.__DOMAIN_UPLOAD();

interface IfImageUpload {
  // props
  inputName: string;
  value: string;
  //data
  loading: boolean;
  image: {
    id: string;
    token: string;
  };
  errorMessage: string;
  allowableType: string[];
  // computed
  imageSrc: string;
  noImage: boolean;
  //vuex
  shop: string;
  //method
  onClick(): void;
  onDelete(event: Event): void;
  readImage(): Promise<void>;
  uploadToServer(img: File): Promise<void>;
  isDisable: boolean;
}

export default defineComponent({
  name: 'ImageUpload',
  props: {
    inputName: {
      type: String,
      default: 'image',
    },
    value: String,
    isDisable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value'],
  data() {
    return {
      loading: false,
      image: {
        id: '',
        token: '',
      },
      errorMessage: '',
      allowableType: ['image/jpeg', 'image/png', 'image/gif', 'image/jpg', 'image/svg+xml'],
    };
  },
  computed: {
    ...mapGetters('app', {
      shop: 'shopifyDomain',
    }),
    imageSrc: {
      get: function () {
        return (this as any).value;
      },
      set: function (newValue: any) {
        this.$emit('update:value', newValue);
      },
    },
    noImage() {
      const src: any = this.value;
      if (src && src.length) return false;
      return true;
    },
    inputAccept() {
      return this.allowableType.join();
    },
  },
  mounted() {},
  methods: {
    onClick() {
      if (this.isDisable) {
        return;
      }
      (this as any).$refs.imageSelect.click();
    },
    async onDelete(event: Event) {
      event.preventDefault();
      this.imageSrc = '';
    },
    async readImage() {
      const files = (this as any).$refs.imageSelect.files;
      if (files.length > 0) {
        this.errorMessage = '';
        const image = files[0];
        const fread = new FileReader();
        fread.onerror = () => {};
        fread.readAsDataURL(image);
        await this.uploadToServer(image);
      }
    },
    async uploadToServer(img: File) {
      const imgSize = img.size;
      const oldImgSrc = this.imageSrc;

      const uploadImageFail = (message = 'Upload image fail') => {
        this.imageSrc = oldImgSrc;

        HELPER.showNotify(message, 'error', 3000, 100);
        this.errorMessage = message;
        return;
      };

      if (imgSize > 3 * 1024 * 1024) {
        uploadImageFail(
          'Your image exceeds maximum file size (3MB). Please crop or compress the image to reduce file size before upload again.',
        );
      } else if (!img.type.length || !this.allowableType.includes(img.type)) {
        uploadImageFail('Upload failed. GemPages only supports these file formats: JPEG, JPG, PNG, SVG, GIF.');
      } else {
        this.loading = true;

        this.imageSrc = window.URL.createObjectURL(img);

        const formData = new FormData();
        // thay đổi name file khi upload
        formData.append('file', img, replaceSpecialCharInImageName(img.name));
        formData.append('shop', this.shop);
        formData.append('app_name', 'gempages');

        const uploadUrl = api.uploadImage().url;

        return POST(`${DOMAIN_UPLOAD}${uploadUrl}`, formData, 'json', false, false)
          .then((res: any) => {
            const data = res.data;
            if (data.success) {
              let seoUrl = data.name ? `${data.url}${encodeURI(data.name)}` : data.url;
              seoUrl = replaceImageToQuality(seoUrl, 'lighter');

              this.imageSrc = seoUrl;
              this.image.id = data.id;
              this.image.token = data.token;
            } else {
              uploadImageFail();
            }
          })
          .catch(() => {
            uploadImageFail();
          })
          .finally(() => {
            this.loading = false;
            (this as any).$refs.imageSelect.value = '';
          });
      }

      this.imageSrc = oldImgSrc;
      this.loading = false;
      (this as any).$refs.imageSelect.value = '';
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';
$image-height: 330px;

input[type='file'] {
  display: none;
}

.image-upload {
  position: relative;
  height: $image-height;

  &__empty {
    background: $highlight2;
    height: $image-height;
    position: relative;
  }

  &__action {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    cursor: pointer;
    &-upload {
      background-color: $neutral5;
      &:hover {
        background-color: $text-dark-high;
      }
    }
    &-delete {
      background-color: $accent-red-new;
      &:hover {
        background-color: $accentRed400;
      }
    }
    &-upload,
    &-delete {
      @include trans-all;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 32px;
      border-radius: 3px;
      padding: 0 24px;
    }
  }

  &__img {
    position: relative;
    height: $image-height;
    overflow-y: hidden;

    &-error {
      background: $light2;

      &-text {
        word-break: break-word;
        overflow-wrap: anywhere;
      }

      .icon-warn-triangle {
        color: $cYellow3;
        font-size: 20px;
      }

      .cancel {
        cursor: pointer;
        margin-left: 0.25rem;
        font-size: 0.75rem;
      }
    }

    img {
      display: block;
      width: 100%;
    }

    .action__hover {
      display: none;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0px;
      top: 0px;
      backdrop-filter: blur(6px);
      background: rgba(0, 0, 0, 0.5);
    }

    &:hover {
      .action__hover {
        display: block;
      }
    }

    &--disable {
      pointer-events: none;
      position: relative;
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        background-color: #f4f4f4;
        z-index: 111;
        width: 100%;
        height: 100%;
        opacity: 0.5;
      }
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #eeeeee80;
  }
  &__disable {
    cursor: inherit;
    background-color: $light3;
    color: $placeholder;
    display: flex;
    align-items: center;
  }
}
.button-tooltip {
  position: relative;
  &__content {
    display: none;
    justify-content: center;
    background: $dark1;
    border-radius: 3px;
    color: $text-dark-high;
    font-size: 12px;
    line-height: 14px;
    padding: 8px;
    width: max-content;
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
  }
  &:hover {
    .button-tooltip__content {
      display: flex;
    }
    &::before {
      border: solid;
      border-color: $dark1 transparent;
      border-width: 4px 4px 0 4px;
      content: '';
      left: 50%;
      transform: translateX(-50%);
      bottom: calc(100% + 4.5px);
      position: absolute;
    }
  }
}
</style>
